import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {Row} from "primereact/row";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {formatParameterValue} from "@reva/shared/number-format";
import {SEGMENT} from "@reva/shared/reva-spec";
import {SEGMENT_BY_OBJECT_TYPE} from "@reva/shared/timelines";

import AppreciationTabInputText from "../../component/appreciation/AppreciationTabInputText";
import ErrorIndicator from "../../component/ErrorIndicator";
import LoadingIndicator from "../../component/LoadingIndicator";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import OpenLayersMap from "../../component/OpenLayersMap";
import {useParameterValue} from "../../component/paramInput/paramHooks";
import {useRevaTranslation, useSelectByCurrentLanguage} from "../../hooks/languageHooks";
import {useMapSettings} from "../../hooks/useMapSettings";
import {selectAddressData} from "../../redux/addressSlice";
import {selectPriceIndexData, selectPriceIndexError, selectPriceIndexLoading} from "../../redux/locationInfoSlice";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectPriceIndexSpecPropertiesModel, selectPriceIndexSpecValuePropertiesModel} from "../../redux/specSelectors";
import {LayerFactory, resolutionFromScale} from "../../util/maps";
import {PERMISSION_RULE_MARKET_PRICEINDEX} from "../../util/permissions";
import {getObjectTypeBySegment, OBJECT_TYPES} from "../../util/timelineAggregation";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";

function TrendIcon({value}) {
    switch (value) {
        case -2:
            return (
                <i
                    style={{fontSize: "0.7rem", color: "var(--text-color-secondary)"}}
                    className="pi pi-arrow-down ml-2"
                ></i>
            );
        case -1:
            return (
                <i
                    style={{fontSize: "0.7rem", color: "var(--text-color-secondary)"}}
                    className="pi pi-arrow-down-right ml-2"
                ></i>
            );
        case 0:
            return (
                <i
                    style={{fontSize: "0.7rem", color: "var(--text-color-secondary)"}}
                    className="pi pi-arrow-right ml-2"
                ></i>
            );
        case 1:
            return (
                <i
                    style={{fontSize: "0.7rem", color: "var(--text-color-secondary)"}}
                    className="pi pi-arrow-up-right ml-2"
                ></i>
            );
        case 2:
            return (
                <i
                    style={{fontSize: "0.7rem", color: "var(--text-color-secondary)"}}
                    className="pi pi-arrow-up ml-2"
                ></i>
            );
        default:
            return null;
    }
}

function PriceIndexNotes() {
    const priceIndex = useSelector(selectPriceIndexData);
    return (
        <div>
            <h2>Erläuterung</h2>
            <p>{priceIndex?.price_index_info}</p>
        </div>
    );
}

function MarketPriceindexOverview() {
    const {t, language} = useRevaTranslation();
    const selectLang = useSelectByCurrentLanguage();
    const dispatch = useDispatch();
    const priceIndexProperties = useSelector(selectPriceIndexSpecPropertiesModel);
    const priceIndexValueProperties = useSelector(selectPriceIndexSpecValuePropertiesModel);

    const queryError = useSelector(selectPriceIndexError);
    const loading = useSelector(selectPriceIndexLoading);
    const priceIndex = useSelector(selectPriceIndexData);
    const segment = useSelector(selectSegment);
    const {parameterValues: buildingProjectValue} = useParameterValue("building_project", "AVM");

    const [tableEntries, setTableEntries] = useState([]);
    const [selectedObjectType, setSelectedObjectType] = useState(
        getObjectTypeBySegment(segment, buildingProjectValue?.activeParameterValue),
    );
    const {sale, rent} = SEGMENT_BY_OBJECT_TYPE[selectedObjectType];
    const segments = [sale, rent].filter(Boolean);

    const priceIndexData = priceIndex?.price_index;

    useEffect(() => {
        // TODO: Transform useEffect to memoized selector instead
        if (priceIndexData) {
            const aggregatedPriceIndex = aggregatePriceIndexData(priceIndexData, priceIndexProperties);
            const rows = aggregatedPriceIndex.filter((v) => segments.indexOf(v.segment) > -1);
            setTableEntries(rows);
        }
    }, [dispatch, priceIndexData, selectedObjectType, language]);

    if (queryError) return <ErrorIndicator errorObject={queryError} titleI18nKey="market.priceindex.error" />;

    if (loading || !priceIndex) return <LoadingIndicator loading={true} className="h-full" />;

    if (!priceIndexProperties) return null;

    const aggredatedPriceIndexData = aggregatePriceIndexData(priceIndexData, priceIndexProperties);

    if (!Array.isArray(priceIndexData)) return null;
    const objectTypeOptions = OBJECT_TYPES.map((o) => ({value: o.value, label: t(o.labelI18n)}));

    //TODO JS: move to data model
    /**
     *
     * @param {PriceIndexItem[]} currentData
     * @returns {{}|{segment: *, location_category_micro: *, locationCategoryMicroLabel: string, existingPropertyPrice: string, existingPropertyPriceLow: null|string, existingPropertyPriceHigh: null|string, existingPropertyPriceTrend: -2|-1|0|1|2, existingPropertyMultiplier: string|null, existingPropertyMultiplierLow: null|string, existingPropertyMultiplierHigh: null|string, existingPropertyMultiplierTrend: -2|-1|0|1|2, newPropertyPrice: string, newPropertyPriceLow: null|string, newPropertyPriceHigh: null|string, newPropertyPriceTrend: -2|-1|0|1|2, newPropertyMultiplier: string|null, newPropertyMultiplierLow: null|string, newPropertyMultiplierHigh: null|string, newPropertyMultiplierTrend: -2|-1|0|1|2}[]}
     */
    function aggregatePriceIndexData(currentData, priceIndexProperties) {
        if (!currentData || !priceIndexProperties) {
            return [];
        }
        // Aggregate data by segment and location_category_micro
        const map = new Map();
        currentData.forEach((priceIndex) => {
            const {segment, location_category_micro, year_of_construction_category, index} = priceIndex;
            const key = JSON.stringify({segment, location_category_micro});
            if (!map.has(key)) map.set(key, []);
            map.get(key).push({segment, location_category_micro, year_of_construction_category, ...index});
        });
        return Array.from(map.entries()).map(([_, values]) => {
            const {segment, location_category_micro} = values[0];
            /** @type {FormatParameterDef} */
            const locationCategoryDef = {
                parameter: "location_category_micro",
                ...priceIndexProperties["location_category_micro"],
            };
            const locationCategoryMicroLabel = formatParameterValue(
                language,
                locationCategoryDef,
                location_category_micro,
            )?.trim();
            const priceParameterDef = {parameter: "price", ...priceIndexValueProperties["price"]};
            const formatPrice = (value, omitUnit) => {
                if (value === null) {
                    return null;
                }
                const formatOverride =
                    [SEGMENT.WHG_M, SEGMENT.BUERO_M].indexOf(segment) < 0 ? "DOUBLE(0-2)" : "DOUBLE(1-2)";
                return formatParameterValue(language, priceParameterDef, value, {omitUnit, formatOverride});
            };
            const multiplierParameterDef = {parameter: "multiplier", ...priceIndexValueProperties["multiplier"]};
            const formatMultiplier = (value, omitUnit) => {
                if (value === null) {
                    return null;
                }
                return formatParameterValue(language, multiplierParameterDef, value, {
                    omitUnit,
                    formatOverride: "DOUBLE(1-1)",
                });
            };

            /** @type {PriceIndexData} */
            const allPropertyData = values.find((v) => v.year_of_construction_category === 0);
            const allPropertyPriceLow = allPropertyData?.price_low;
            const allPropertyPriceHigh = allPropertyData?.price_high;
            const allPropertyPrice = `${formatPrice(allPropertyPriceLow, true)} - ${formatPrice(allPropertyPriceHigh, true)}`;
            const allPropertyPriceTrend = allPropertyData?.trend;
            const allPropertyMultiplierLow = allPropertyData?.multiplier_low;
            const allPropertyMultiplierHigh = allPropertyData?.multiplier_high;
            const allPropertyMultiplier = allPropertyMultiplierLow
                ? `${formatMultiplier(allPropertyMultiplierLow, true)} - ${formatMultiplier(allPropertyMultiplierHigh, true)}`
                : null;
            const allPropertyMultiplierTrend = allPropertyData?.multiplier_trend;

            /** @type {PriceIndexData} */
            const existingPropertyData = values.find((v) => v.year_of_construction_category === 1);
            const existingPropertyPriceLow = existingPropertyData?.price_low;
            const existingPropertyPriceHigh = existingPropertyData?.price_high;
            const existingPropertyPrice = `${formatPrice(existingPropertyPriceLow, true)} - ${formatPrice(existingPropertyPriceHigh, true)}`;
            const existingPropertyPriceTrend = existingPropertyData?.trend;
            const existingPropertyMultiplierLow = existingPropertyData?.multiplier_low;
            const existingPropertyMultiplierHigh = existingPropertyData?.multiplier_high;
            const existingPropertyMultiplier = existingPropertyMultiplierLow
                ? `${formatMultiplier(existingPropertyMultiplierLow, true)} - ${formatMultiplier(existingPropertyMultiplierHigh, true)}`
                : null;
            const existingPropertyMultiplierTrend = existingPropertyData?.multiplier_trend;

            /** @type {PriceIndexData} */
            const newPropertyData = values.find((v) => v.year_of_construction_category === 2);
            const newPropertyPriceLow = newPropertyData?.price_low;
            const newPropertyPriceHigh = newPropertyData?.price_high;
            const newPropertyPrice = `${formatPrice(newPropertyPriceLow, true)} - ${formatPrice(newPropertyPriceHigh)}`;
            const newPropertyPriceTrend = newPropertyData?.trend;
            const newPropertyMultiplierLow = newPropertyData?.multiplier_low;
            const newPropertyMultiplierHigh = newPropertyData?.multiplier_high;
            const newPropertyMultiplier = newPropertyMultiplierLow
                ? `${formatMultiplier(newPropertyMultiplierLow, true)} - ${formatMultiplier(newPropertyMultiplierHigh, true)}`
                : null;
            const newPropertyMultiplierTrend = newPropertyData?.multiplier_trend;

            return {
                segment,
                location_category_micro,
                locationCategoryMicroLabel,

                allPropertyPrice,
                allPropertyPriceLow: formatPrice(allPropertyPriceLow, true),
                allPropertyPriceHigh: formatPrice(allPropertyPriceHigh, true),
                allPropertyPriceTrend,
                allPropertyMultiplier,
                allPropertyMultiplierLow: formatMultiplier(allPropertyMultiplierLow, true),
                allPropertyMultiplierHigh: formatMultiplier(allPropertyMultiplierHigh, true),
                allPropertyMultiplierTrend,

                existingPropertyPrice,
                existingPropertyPriceLow: formatPrice(existingPropertyPriceLow, true),
                existingPropertyPriceHigh: formatPrice(existingPropertyPriceHigh, true),
                existingPropertyPriceTrend,
                existingPropertyMultiplier,
                existingPropertyMultiplierLow: formatMultiplier(existingPropertyMultiplierLow, true),
                existingPropertyMultiplierHigh: formatMultiplier(existingPropertyMultiplierHigh, true),
                existingPropertyMultiplierTrend,

                newPropertyPrice,
                newPropertyPriceLow: formatPrice(newPropertyPriceLow, true),
                newPropertyPriceHigh: formatPrice(newPropertyPriceHigh, true),
                newPropertyPriceTrend,
                newPropertyMultiplier,
                newPropertyMultiplierLow: formatMultiplier(newPropertyMultiplierLow, true),
                newPropertyMultiplierHigh: formatMultiplier(newPropertyMultiplierHigh, true),
                newPropertyMultiplierTrend,

                priceUnit: selectLang(priceParameterDef.unitDe, priceParameterDef.unitEn),
                multiplierUnit: selectLang(multiplierParameterDef.unitDe, multiplierParameterDef.unitEn),
            };
        });
    }

    const changeObjectType = (event) => {
        if (!event.value) {
            setTableEntries([]);
            return;
        }
        setSelectedObjectType(event.value);
        if (!priceIndexData) {
            setTableEntries([]);
            return;
        }
        setTableEntries(aggredatedPriceIndexData.filter((v) => v.segment === event.value));
    };
    return (
        <div>
            <ObjectTypeDropdown
                objectType={selectedObjectType}
                options={objectTypeOptions}
                onChange={changeObjectType}
            />
            {segments.map((segment) => (
                <MarketPriceIndexSegmentTables key={segment} segment={segment} tableEntries={tableEntries} />
            ))}
        </div>
    );
}

function ObjectTypeDropdown({objectType, options, onChange}) {
    const {t} = useTranslation();
    const dropDownTitle = t("market.priceindex.object_type");

    return (
        <div className="element-panel pb-4">
            <label htmlFor="priceindex-object-type" className="input-label font-bold !mt-2 block">
                {dropDownTitle}
            </label>
            <Dropdown
                inputId="priceindex-object-type"
                value={objectType}
                options={options}
                className="w-full md:w-[26rem]"
                onChange={onChange}
            />
        </div>
    );
}

function MarketPriceindexMap() {
    const {t, language} = useRevaTranslation();
    const addressData = useSelector(selectAddressData);
    const {initialView, onMapMoveEnd} = useMapSettings("priceindex_map_settings");

    if (!addressData) return null;

    /**
     * Map Model from address.
     * @type {MapModel}
     */
    const mapModel = {
        view: {
            lat: initialView?.lat ?? addressData.lat,
            lon: initialView?.lon ?? addressData.lon,
            // Start auf rundem Maßstab 1:50.000, sofern keine gespeicherte Ansicht existiert
            ...(initialView?.zoom != null
                ? {zoom: initialView.zoom}
                : {resolution: resolutionFromScale(50000, addressData.lat)}),
            resetCenterOnChange: true,
        },
        layers: [
            LayerFactory.createGeoserverWmsLayer(
                "wohnlagen_mikro_marktreports",
                addressData,
                undefined,
                "markt_reports_kkz",
                undefined,
                {
                    params: {viewparams: `kkz:${addressData.address.district_code}`},
                    title: "Preisindex auf Kreisebene",
                    type: "base",
                    visible: true,
                },
            ),
            LayerFactory.createMarkerLayer(addressData.address, language),
        ],
        eventHandlers: {
            moveend_event: onMapMoveEnd,
        },
        scaleLine: {enabled: true},
    };

    return (
        <div className="OpenLayersMapBackdrop w-full h-full">
            <OpenLayersMap
                model={mapModel}
                legend={<PriceIndexNotes />}
                legendOptions={{buttonLabel: t("general.map.note")}}
            />
        </div>
    );
}

function MarketPriceAllPropertyTable({tableEntries, locationTitle, allPropertyTitle, allPropertyField}) {
    const {t} = useTranslation();
    const selectLang = useSelectByCurrentLanguage();
    const valueRangeHeader = selectLang("Wertebereich", "Value range");
    const effectiveAllPropertyTitle =
        tableEntries && tableEntries.length > 0 && allPropertyField.indexOf("Price") >= 0
            ? `${allPropertyTitle} in ${tableEntries[0]?.priceUnit}`
            : allPropertyTitle;
    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header={locationTitle} align={"center"} />
                <Column header={effectiveAllPropertyTitle} align={"right"} colSpan={4} />
                <Column colSpan={4} />
            </Row>
            <Row>
                <Column />
                <Column colSpan={3} header={valueRangeHeader} align={"right"} />
                <Column header={"Trend"} align={"center"} />
                <Column colSpan={4} />
            </Row>
        </ColumnGroup>
    );
    return (
        <div className={"element-panel"}>
            <DataTable
                value={tableEntries ?? []}
                headerColumnGroup={headerGroup}
                emptyMessage={t("value.emptyMessage")}
                size="small"
            >
                <Column
                    field="locationCategoryMicroLabel"
                    align={"center"}
                    bodyStyle={{width: "25%"}}
                    bodyClassName={(rowData) => `location-micro-category-${rowData.location_category_micro}`}
                />

                <Column field={"allPropertyPriceLow"} align={"right"} bodyStyle={{width: "25%"}} />
                <Column bodyStyle={{width: "fit-content"}} body={"-"} />
                <Column field={"allPropertyPriceHigh"} align={"right"} bodyStyle={{width: "fit-content"}} />
                <Column
                    field={"allPropertyPriceTrend"}
                    align={"center"}
                    body={(rowData) => <TrendIcon value={rowData["allPropertyPriceTrend"]} />}
                    bodyStyle={{width: "fit-content"}}
                />
                <Column colSpan={4} bodyStyle={{width: "40%"}} />
            </DataTable>
        </div>
    );
}

function MarketPriceExistingAndNewPropertyTable({
    tableEntries,
    locationTitle,
    existingPropertiesTitle,
    existingPropertiesField,
    newPropertiesTitle,
    newPropertiesField,
}) {
    const {t} = useTranslation();
    const selectLang = useSelectByCurrentLanguage();
    const low = (baseField) => `${baseField}Low`;
    const high = (baseField) => `${baseField}High`;
    const trend = (baseField) => `${baseField}Trend`;
    const unit =
        tableEntries && tableEntries.length > 0 && existingPropertiesField.indexOf("Price") >= 0
            ? tableEntries[0]?.priceUnit
            : null;

    const valueRangeHeader = selectLang("Wertebereich", "Value range");
    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header={locationTitle} align={"center"} />
                <Column />
                <Column
                    header={!unit ? existingPropertiesTitle : `${existingPropertiesTitle} in ${unit}`}
                    align={"left"}
                    colSpan={3}
                />
                <Column />
                <Column
                    header={!unit ? newPropertiesTitle : `${newPropertiesTitle} in ${unit}`}
                    align={"left"}
                    colSpan={3}
                    pt={{headerContent: {className: "w-36"}}}
                />
            </Row>
            <Row>
                <Column align={"center"} />
                <Column colSpan={3} header={valueRangeHeader} align={"right"} />
                <Column header={"Trend"} align={"center"} />
                <Column colSpan={3} header={valueRangeHeader} align={"right"} />
                <Column header={"Trend"} align={"center"} />
            </Row>
        </ColumnGroup>
    );

    return (
        <div className={"element-panel"}>
            <DataTable
                value={tableEntries ?? []}
                headerColumnGroup={headerGroup}
                emptyMessage={t("value.emptyMessage")}
                size="small"
            >
                <Column
                    field="locationCategoryMicroLabel"
                    align={"center"}
                    bodyStyle={{width: "25%"}}
                    bodyClassName={(rowData) => `location-micro-category-${rowData.location_category_micro}`}
                />

                <Column field={low(existingPropertiesField)} align={"right"} bodyStyle={{width: "25%"}} />
                <Column bodyStyle={{width: "fit-content"}} body={"-"} />
                <Column field={high(existingPropertiesField)} align={"right"} bodyStyle={{width: "fit-content"}} />
                <Column
                    field={existingPropertiesField}
                    align={"center"}
                    body={(rowData) => <TrendIcon value={rowData[trend(existingPropertiesField)]} />}
                    bodyStyle={{width: "fit-content"}}
                />

                <Column field={low(newPropertiesField)} align={"right"} bodyStyle={{width: "25%"}} />
                <Column bodyStyle={{width: "fit-content"}} body={"-"} />
                <Column field={high(newPropertiesField)} align={"right"} bodyStyle={{width: "fit-content"}} />
                <Column
                    field={newPropertiesField}
                    align={"center"}
                    bodyStyle={{width: "fit-content"}}
                    body={(rowData) => <TrendIcon value={rowData[trend(newPropertiesField)]} />}
                />
            </DataTable>
        </div>
    );
}

function formatCategory(category, selectLang) {
    const {shortNameDe, shortNameEn} = category;
    const string = selectLang(shortNameDe, shortNameEn);
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function MarketPriceIndexSegmentTables({segment, tableEntries}) {
    const {t} = useTranslation();
    const priceIndexProperties = useSelector(selectPriceIndexSpecPropertiesModel);
    const selectLang = useSelectByCurrentLanguage();
    const tableTitle = t(`market.priceindex.price_table_title.${segment}`);
    const locationTitle = selectLang(
        priceIndexProperties["location_category_micro"].nameDe,
        priceIndexProperties["location_category_micro"].nameEn,
    );
    const yearOfConstructionCategories = priceIndexProperties["year_of_construction_category"].categories;
    const allPropertyTitle = formatCategory(yearOfConstructionCategories[0], selectLang);
    const existingPropertiesTitle = formatCategory(yearOfConstructionCategories[1], selectLang);
    const newPropertiesTitle = formatCategory(yearOfConstructionCategories[2], selectLang);
    const multiplierTitle = t(`market.priceindex.multiplier_table_title.${segment}`);
    const values = tableEntries.filter((v) => v.segment === segment);
    const multiplierValues = values?.filter((v) => v.existingPropertyMultiplier && v.newPropertyMultiplier) ?? [];
    if (values.find((v) => v.allPropertyPriceLow && v.allPropertyPriceHigh && v.allPropertyPriceTrend))
        return (
            <>
                <h3 className="element-title table-title mt-3 mb-3 ml-2">{tableTitle}</h3>
                <MarketPriceAllPropertyTable
                    tableEntries={values}
                    locationTitle={locationTitle}
                    allPropertyTitle={allPropertyTitle}
                    allPropertyField={"allPropertyPrice"}
                />
            </>
        );

    return (
        <>
            <h3 className="element-title table-title mt-3 mb-3 ml-2">{tableTitle}</h3>
            <MarketPriceExistingAndNewPropertyTable
                tableEntries={values}
                locationTitle={locationTitle}
                existingPropertiesTitle={existingPropertiesTitle}
                existingPropertiesField={"existingPropertyPrice"}
                newPropertiesTitle={newPropertiesTitle}
                newPropertiesField={"newPropertyPrice"}
            />
            {multiplierValues.length > 0 && (
                <>
                    <h3 className="element-title table-title mt-3 mb-3 ml-2">{multiplierTitle}</h3>
                    <MarketPriceExistingAndNewPropertyTable
                        tableEntries={multiplierValues}
                        locationTitle={locationTitle}
                        existingPropertiesTitle={existingPropertiesTitle}
                        existingPropertiesField={"existingPropertyMultiplier"}
                        newPropertiesTitle={newPropertiesTitle}
                        newPropertiesField={"newPropertyMultiplier"}
                    />
                </>
            )}
        </>
    );
}

/**
 * Tab for the market price index.
 * @returns {React.JSX.Element}
 */
export function TabMarketPriceindex() {
    return (
        <div className="flex-1">
            <MissingPermissionIndicator permissionRule={PERMISSION_RULE_MARKET_PRICEINDEX}>
                <PersistenceFinalMessage />
                <SnapshotMessage />
                <AppreciationTabInputText page="market" tab="priceindex" />
                <div className="MapSplit h-full priceindex-wide-left">
                    <div className="MapSplitLeft">
                        <MarketPriceindexOverview />
                    </div>
                    <div className="MapSplitRight OpenLayersMapBackdrop">
                        <MarketPriceindexMap />
                    </div>
                </div>
            </MissingPermissionIndicator>
        </div>
    );
}

export default TabMarketPriceindex;
